var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { Autocomplete, Box, CircularProgress, TextField } from '@mui/material';
import { autoCompleteSearchStyle } from './AutoComplete.style';
var isPopupElement = function (element) {
    if (!(element instanceof HTMLElement)) {
        return false;
    }
    return Boolean(element.closest('.MuiAutocomplete-listbox') ||
        element.closest('.MuiAutocomplete-popper'));
};
var isCalendarLikeElement = function (element) {
    if (!(element instanceof HTMLElement)) {
        return false;
    }
    return Boolean(element.closest('.MuiPickersPopper-root') ||
        element.closest('.MuiDialog-root') ||
        element.closest('[data-testid="controlledDatePicker"]'));
};
var AutoComplete = function (_a) {
    var options = _a.options, renderOption = _a.renderOption, open = _a.open, onOpen = _a.onOpen, onClose = _a.onClose, id = _a.id, filterOptions = _a.filterOptions, value = _a.value, onChange = _a.onChange, onInputChange = _a.onInputChange, loading = _a.loading, _b = _a.disabled, disabled = _b === void 0 ? false : _b, getOptionDisabled = _a.getOptionDisabled, placeholder = _a.placeholder, noOptionsText = _a.noOptionsText, _c = _a.getOptionLabel, getOptionLabel = _c === void 0 ? function (option) { return option.fullName; } : _c, _d = _a.isOptionEqualToValue, isOptionEqualToValue = _d === void 0 ? function (option, value) { return option === value; } : _d, _e = _a.isOldStyle, isOldStyle = _e === void 0 ? false : _e, _f = _a.error, error = _f === void 0 ? false : _f, _g = _a.isFixedWidth, isFixedWidth = _g === void 0 ? true : _g, _h = _a.multiple, multiple = _h === void 0 ? false : _h, _j = _a.hideTags, hideTags = _j === void 0 ? false : _j, _k = _a.disablePortal, disablePortal = _k === void 0 ? true : _k, _l = _a.inlineOptions, inlineOptions = _l === void 0 ? false : _l;
    var className = autoCompleteSearchStyle({ isOldStyle: isOldStyle, error: error }).classes;
    var containerRef = React.useRef(null);
    var suppressBlurCloseRef = React.useRef(false);
    var ignoreNextBlurRef = React.useRef(false);
    var handleClose = React.useCallback(function (event, reason) {
        var relatedTarget = event === null || event === void 0 ? void 0 : event.relatedTarget;
        if (reason === 'blur' && isPopupElement(relatedTarget)) {
            return;
        }
        document.body.style.overflow = 'auto';
        onClose(event, reason);
    }, [onClose]);
    React.useEffect(function () {
        var handleOutsideInteraction = function (event) {
            var _a, _b;
            var target = event.target;
            if (!containerRef.current || !target) {
                return;
            }
            var path = (_b = (_a = event.composedPath) === null || _a === void 0 ? void 0 : _a.call(event)) !== null && _b !== void 0 ? _b : [];
            var isInsideContainer = path.includes(containerRef.current);
            var isPopupInteraction = path.some(function (node) { return node instanceof HTMLElement && node.closest('.MuiAutocomplete-listbox'); });
            var isCalendarInteraction = path.some(function (node) {
                return node instanceof HTMLElement &&
                    (node.closest('.MuiPickersPopper-root') ||
                        node.closest('.MuiDialog-root') ||
                        node.closest('[data-testid="controlledDatePicker"]'));
            });
            if (isInsideContainer || isPopupInteraction) {
                suppressBlurCloseRef.current = true;
                return;
            }
            suppressBlurCloseRef.current = false;
            ignoreNextBlurRef.current = true;
            handleClose(event, 'outsideClick');
        };
        var eventTypes = ['mousedown', 'touchstart', 'pointerdown'];
        eventTypes.forEach(function (eventType) {
            window.addEventListener(eventType, handleOutsideInteraction);
        });
        return function () {
            eventTypes.forEach(function (eventType) {
                window.removeEventListener(eventType, handleOutsideInteraction);
            });
        };
    }, [handleClose]);
    return (_jsx(Box, { ref: containerRef, children: _jsx(Autocomplete, { id: id, onBlur: function (event) {
                if (ignoreNextBlurRef.current) {
                    ignoreNextBlurRef.current = false;
                    return;
                }
                if (suppressBlurCloseRef.current) {
                    suppressBlurCloseRef.current = false;
                    return;
                }
                var relatedTarget = event.relatedTarget;
                if (isPopupElement(relatedTarget) || isCalendarLikeElement(relatedTarget)) {
                    return;
                }
                handleClose(event, 'blur');
            }, classes: __assign({}, className), blurOnSelect: !multiple, disableCloseOnSelect: multiple, autoHighlight: true, options: options, sx: isFixedWidth ? { width: 300 } : {}, open: open, value: multiple ? value || [] : value && (typeof value === 'object' ? value : null), multiple: multiple, renderTags: hideTags ? function () { return null; } : undefined, componentsProps: inlineOptions ? {
                popper: {
                    sx: {
                        position: 'static !important',
                        transform: 'none !important',
                        width: '100% !important',
                    },
                },
                paper: {
                    sx: {
                        maxHeight: 'none',
                        overflow: 'visible',
                        boxShadow: 'none',
                        backgroundColor: '#F8FAF6',
                    },
                },
            } : undefined, onChange: onChange, onOpen: onOpen, onClose: function (event, reason) {
                var relatedTarget = event === null || event === void 0 ? void 0 : event.relatedTarget;
                if (reason === 'blur' && (isPopupElement(relatedTarget) || isCalendarLikeElement(relatedTarget))) {
                    return;
                }
                handleClose(event, reason);
            }, filterOptions: filterOptions, getOptionLabel: getOptionLabel, renderOption: renderOption, onInputChange: onInputChange, autoComplete: true, disablePortal: disablePortal, disableClearable: !multiple, loading: loading, disabled: disabled, getOptionDisabled: getOptionDisabled, renderInput: function (params) { return (_jsx(TextField, __assign({ variant: 'standard' }, params, { InputProps: __assign(__assign({}, params.InputProps), { disableUnderline: true, endAdornment: (_jsxs(React.Fragment, { children: [loading ? (_jsx(CircularProgress, { color: 'inherit', size: 20 })) : null, params.InputProps.endAdornment] })) }), inputProps: __assign(__assign({}, params.inputProps), { classes: { icon: 'red' }, placeholder: placeholder }) }))); }, ListboxProps: __assign({ role: 'list-box' }, (inlineOptions && {
                sx: {
                    maxHeight: 'none',
                    overflowY: 'visible',
                },
            })), noOptionsText: noOptionsText, isOptionEqualToValue: isOptionEqualToValue }) }));
};
export default AutoComplete;
